import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';

import { SituacaoContaEnum } from '../../../../shared/enums/SituacaoConta.enum';
import { ContaService } from '../../../../shared/services/conta/conta.service';
import { ClientNotificationService } from '../../../../shared/services/client-notification/client-notification.service';
import { TokenService } from '../../../../shared/services/token/token.service';
import { VendaService } from '../../../../shared/services/venda/venda.service';

declare function unescape(s: string): string;

@Component({
  selector: 'app-pos',
  templateUrl: './pos.component.html',
  styleUrls: ['./pos.component.scss']
})
export class PosComponent implements OnInit, OnDestroy {

  public sales: any;
  public tokenId: string;
  public token: any;
  public validationResultError: any;
  public client: string;
  public accountId: string;
  public account: any;
  public profilePicture: string;
  public bloqueio: boolean;
  public showModal: boolean;
  public msgBlock: string;
  public accountNotAllowed: boolean;
  public origemActuar: string;
  public loading = true;
  public isCobrancaAberta = false;
  public isCobrancaProcessada = false;
  public isCobrancaComProblema = false;
  public isVendaCancelada = false;
  public paymentData: any;
  public statusTitle = '';
  public statusDescription = '';
  public cancelandoVenda = false;
  public cancelamentoErro = false;
  public cancelamentoErroMensagem = '';

  /** Timer countdown variables */
  public timerMinutes = 3;
  public timerSeconds = 0;
  public timerExpired = false;

  private notificationSubscription: Subscription;
  private timerSubscription: Subscription;
  private paymentIdentifiers: string[] = [];

  constructor(
    private tokenService: TokenService,
    private router: Router,
    private route: ActivatedRoute,
    private contaService: ContaService,
    private clientNotificationService: ClientNotificationService,
    private vendaService: VendaService,
  ) { }

  ngOnInit() {

    this.route.queryParams.subscribe((param) => {
      this.tokenId = param['tokenId'];
      if (this.getURLParameters('backUrl')) {
        this.origemActuar = this.getURLParameters('backUrl');
      }
      this.getToken();
    });
  }

  ngOnDestroy() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  getToken() {
    if (!this.tokenId) {
      this.router.navigate(['/checkout/404']);
      return;
    }

    this.loading = true;
    this.tokenService.obterToken(this.tokenId).subscribe((data: any) => {
      this.validationResultError = data &&
        data.Validacao &&
        data.Validacao.Erros &&
        data.Validacao.Erros.length > 0 ?
        data.Validacao.Erros[0] :
        null;

      this.getAccount(data.ContaId);

      this.token = data;
      this.sales = this.sortSales(data.Vendas);
      this.paymentData = data.Vendas[0];
      this.paymentIdentifiers = this.extractPaymentIdentifiers(this.paymentData);

      this.isVendaCancelada = false;
      this.cancelandoVenda = false;
      this.cancelamentoErro = false;
      this.cancelamentoErroMensagem = '';

      this.accountId = data.ContaId;
      this.client = data.Vendas && data.Vendas[0] ? data.Vendas[0].Cliente : null;
      console.log('teste')
      this.applyPaymentStatus(this.paymentData);
      this.updateStatusMessages();

      if (this.validationResultError) {
        this.loading = false;
        return;
      }

      this.loading = false;

      if (this.isCobrancaAberta && !this.isCobrancaComProblema && data.ContaId) {
        this.verificarSePagamentoFoiProcessado(data.ContaId);
        this.startTimer();
      }
    }, (err) => {
      console.error(err);
      this.router.navigate(['/checkout/404']);
    });
  }

  getAccount(contaId) {
    if (!contaId) {
      return;
    }

    this.contaService.obterConta(contaId).subscribe((data: any) => {
      this.account = data;

      if (this.account && this.account.Objetos && this.account.Objetos.length > 0) {
        this.account.Objetos.forEach((objeto) => {
          if (objeto.TipoObjeto === 3) {
            this.profilePicture = 'https://' + objeto.CaminhoObjeto;
          }
        });
      }

      if (this.account && this.account.Situacao == SituacaoContaEnum.Bloqueada) {
        this.bloqueio = true;
      }

      if (data && data.TipoCadastro === 'PagueLa') {
        this.checkAccount();
      }
    }, (err) => {
      console.error(err);
    });
  }

  sortSales(sales) {
    if (!sales) {
      return [];
    }

    let parceladas = sales.filter((sale) => sale.Parcelas !== null);
    let recorrentes = sales.filter((sale) => sale.Recorrencia);

    recorrentes = recorrentes.sort(function (a, b) {
      if (
        b.Recorrencia &&
        !b.Recorrencia.QuantidadeRecorrencia
      ) { return 1; }

      if (
        a.Recorrencia &&
        a.Recorrencia.QuantidadeRecorrencia &&
        b.Recorrencia &&
        b.Recorrencia.QuantidadeRecorrencia &&
        a.Recorrencia.QuantidadeRecorrencia > b.Recorrencia.QuantidadeRecorrencia
      ) {
        return 1;
      }

      if (
        a.Recorrencia &&
        a.Recorrencia.QuantidadeRecorrencia &&
        b.Recorrencia &&
        b.Recorrencia.QuantidadeRecorrencia &&
        a.Recorrencia.QuantidadeRecorrencia < b.Recorrencia.QuantidadeRecorrencia
      ) { return -1; }

      return 0;
    });

    parceladas = parceladas.sort(function (a, b) {
      if (a.Parcelas > b.Parcelas) { return 1; }
      if (a.Parcelas < b.Parcelas) { return -1; }
      return 0;
    });

    return sales;
  }

  checkAccount() {
    if (!this.account) {
      return;
    }

    let isIncomplete = false;

    if (
      (this.account.TipoConta && this.account.TipoConta === 'Fisica') &&
      (!this.account.CpfCnpj || this.account.CpfCnpj.length === 0 ||
        !this.account.Nome || this.account.Nome.length === 0 ||
        !this.account.Sobrenome || this.account.Sobrenome.length === 0)
    ) {
      isIncomplete = true;
    }

    if (
      (this.account.TipoConta && this.account.TipoConta === 'Juridica') &&
      (!this.account.CpfCnpj || this.account.CpfCnpj.length === 0 ||
        !this.account.RazaoSocial || this.account.RazaoSocial.length === 0 ||
        !this.account.Fantasia || this.account.Fantasia.length === 0 ||
        !this.account.Nome || this.account.Nome.length === 0)
    ) {
      isIncomplete = true;
    }

    if (
      !this.account.Enderecos || this.account.Enderecos.length === 0 ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Cep || this.account.Enderecos[0].Cep.length === 0) ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Logradouro || this.account.Enderecos[0].Logradouro.length === 0) ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Numero || this.account.Enderecos[0].Numero.length === 0) ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Bairro || this.account.Enderecos[0].Bairro.length === 0) ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Cidade || this.account.Enderecos[0].Cidade.length === 0) ||
      this.account.Enderecos.length > 0 && (!this.account.Enderecos[0].Estado || this.account.Enderecos[0].Estado.length === 0)
    ) {
      isIncomplete = true;
    }

    if (
      !this.account.InformacoesBancarias || this.account.InformacoesBancarias.length === 0 ||
      this.account.InformacoesBancarias.length > 0 &&
      (!this.account.InformacoesBancarias[0].Banco || this.account.InformacoesBancarias[0].Banco.length === 0) ||
      this.account.InformacoesBancarias.length > 0 &&
      (!this.account.InformacoesBancarias[0].Agencia || this.account.InformacoesBancarias[0].Agencia.length === 0) ||
      this.account.InformacoesBancarias.length > 0 &&
      (!this.account.InformacoesBancarias[0].NumeroConta || this.account.InformacoesBancarias[0].NumeroConta.length === 0) ||
      this.account.InformacoesBancarias.length > 0 &&
      (!this.account.InformacoesBancarias[0].ContaDigito || this.account.InformacoesBancarias[0].ContaDigito.length === 0)
    ) {
      isIncomplete = true;
    }

    if (this.account && this.account.Situacao != SituacaoContaEnum.Ativa) {
      this.openModal('O seu cadastro está pendente de aprovação.');
      return;
    }

    if (isIncomplete === true) {
      this.openModal('Os dados da sua conta estão incompletos. Faça o login e preencha os dados que estão faltando.');
      return;
    }
  }

  openModal(msg) {
    this.showModal = true;
    this.msgBlock = msg;
  }

  closeModal() {
    this.showModal = false;
    this.msgBlock = '';
  }

  verificarSePagamentoFoiProcessado(contaId: string) {
    if (!contaId) {
      return;
    }

    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    this.notificationSubscription = this.clientNotificationService.openConnection(['onCobrancaNotification'], contaId).subscribe((data) => {
      console.log(data);
      const cobrancaEvent = data && data.eventData ? data.eventData.Cobranca : null;
      if (!cobrancaEvent || !this.paymentIdentifiers || this.paymentIdentifiers.length === 0) {
        return;
      }

      if (data?.eventData?.Cobranca?.tokenId === this.paymentData?.TokenId) {
        this.isCobrancaAberta = false;
        this.isCobrancaProcessada = true;
        this.isCobrancaComProblema = false;
        this.stopTimer();
        this.updateStatusMessages();

        if (this.origemActuar) {
          setTimeout(() => {
            window.location.href = `${this.origemActuar}?action=CR`;
          }, 5000);
        }
      }
    });
  }

  getURLParameters(paramName) {
    const sURL = window.document.URL.toString();
    if (sURL.indexOf('?') > 0) {
      const arrParams = sURL.split('?');
      const arrURLParams = arrParams[1].split('&');
      const arrParamNames = new Array(arrURLParams.length);
      const arrParamValues = new Array(arrURLParams.length);

      let i = 0;
      for (i = 0; i < arrURLParams.length; i++) {
        const sParam = arrURLParams[i].split('=');
        arrParamNames[i] = sParam[0];
        if (sParam[1] !== '') {
          arrParamValues[i] = unescape(sParam[1]);
        } else {
          arrParamValues[i] = null;
        }
      }

      for (i = 0; i < arrURLParams.length; i++) {
        if (arrParamNames[i] === paramName) {
          return arrParamValues[i];
        }
      }
      return null;
    }
  }

  private applyPaymentStatus(payment: any) {
    this.isCobrancaAberta = false;
    this.isCobrancaProcessada = false;
    this.isCobrancaComProblema = false;
    this.isVendaCancelada = false;
    console.log(payment);
    if (!payment) {
      return;
    }

    const situacao = payment.Situacao || payment.Status || '';
    console.log(situacao);
    const normalizedSituacao = (situacao || '').toString().toUpperCase();

    const openSituations = ['ABERTA', 'ABERTO', 'PENDENTE', 'AGUARDANDO_PAGAMENTO'];
    const processedSituations = ['CONCLUIDA', 'CONCLUIDO', 'RECEBIDO', 'PAGO', 'PAGA', 'APROVADO', 'APROVADA', 'LIQUIDADO', 'LIQUIDADA', 'FINALIZADO'];
    const problemSituations = ['NAO_REALIZADO', 'DEVOLVIDO', 'REMOVIDA_PELO_USUARIO_RECEBEDOR', 'REMOVIDA_PELO_PSP', 'CANCELADA', 'CANCELADO', 'NEGADA', 'RECUSADO', 'FALHA', 'ERRO'];

    if (problemSituations.indexOf(normalizedSituacao) !== -1) {
      this.isCobrancaComProblema = true;
      return;
    }

    if (processedSituations.indexOf(normalizedSituacao) !== -1) {
      this.isCobrancaProcessada = true;
      return;
    }

    if (openSituations.indexOf(normalizedSituacao) !== -1 || !normalizedSituacao) {
      this.isCobrancaAberta = true;
    }
  }

  private updateStatusMessages() {
    if (this.isVendaCancelada) {
      this.statusTitle = 'Venda cancelada';
      this.statusDescription = 'Solicitamos o cancelamento da venda no terminal. Retornando ao sistema Actuar...';
      return;
    }

    if (this.isCobrancaComProblema) {
      this.statusTitle = 'Esta venda foi cancelada';
      this.statusDescription = 'Caso ainda queira receber esta venda, refaça o recebimento por maquininha Pague.lá.';
      return;
    }

    if (!this.isCobrancaAberta && this.isCobrancaProcessada) {
      this.statusTitle = 'Pagamento aprovado!';
      this.statusDescription = 'Recebemos a confirmação da maquininha. Você já pode prosseguir com o atendimento.';
      return;
    }

    this.statusTitle = 'Aguardando pagamento na maquininha';
    this.statusDescription = 'Assim que a transação for concluída na maquininha, atualizaremos automaticamente esta tela.';
  }

  private extractPaymentIdentifiers(payment: any): string[] {
    if (!payment) {
      return [];
    }

    const identifiers = [
      payment.CobrancaPixId,
      payment.BoletoId,
      payment.CobrancaId,
      payment.TransacaoId,
      payment.TransacaoPosId,
      payment.TransacaoMaquininhaId,
      payment.Id,
      payment.ID,
      payment.TokenId
    ];

    return identifiers
      .filter((value) => value !== undefined && value !== null)
      .map((value) => value.toString());
  }

  /**
   * Starts a 3-minute countdown timer
   * When timer expires, changes the cancel button to "Voltar ao Perfil do Aluno"
   */
  private startTimer() {
    this.timerMinutes = 1;
    this.timerSeconds = 0;
    this.timerExpired = false;

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.timerSeconds > 0) {
        this.timerSeconds--;
      } else if (this.timerMinutes > 0) {
        this.timerMinutes--;
        this.timerSeconds = 59;
      } else {
        this.cancelandoVenda = true;
        this.cancelamentoErro = false;
        this.cancelamentoErroMensagem = '';
        this.timerExpired = true;
        this.timerSubscription.unsubscribe();
        const vendaId = this.obterVendaId();
        if (vendaId) {
          this.vendaService.cancelarVendaTerminal(vendaId).subscribe(() => {
            this.cancelandoVenda = false;
            this.stopTimer();
            this.cancelamentoFinalizado();
            this.statusTitle = 'Venda expirada';
            this.statusDescription = 'Caso ainda queira receber esta venda, refaça o recebimento por maquininha Pague.lá.';
          },
            (error) => {
              this.cancelandoVenda = false;
              this.cancelamentoErro = true;
              this.cancelamentoErroMensagem = 'Não foi possível cancelar a venda. Tente novamente.';
            }
          );
        }
      }
    });
  }

  /**
   * Stops the timer countdown
   */
  private stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  /**
   * Navigates back to the student profile (Actuar)
   */
  voltarParaPerfilAluno() {
    if (this.origemActuar) {
      window.location.href = this.origemActuar;
    } else {
      this.router.navigate(['/']);
    }
  }

  /**
   * Confirms exit and navigates back after cancelling the sale
   */
  confirmExit() {
    this.cancelarVendaTerminal({ withoutTimer: true });
  }

  /**
   * Returns formatted timer string (MM:SS)
   */
  get formattedTimer(): string {
    const mins = this.timerMinutes.toString().padStart(2, '0');
    const secs = this.timerSeconds.toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }

  public cancelamentoFinalizado() {
    this.cancelandoVenda = false;
    this.isVendaCancelada = true;
    this.isCobrancaAberta = false;
    this.isCobrancaProcessada = false;
    this.isCobrancaComProblema = false;
    this.stopTimer();

    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  cancelarVendaTerminal({ withoutTimer = false }: { withoutTimer?: boolean }) {
    const vendaId = this.obterVendaId();

    if (!vendaId) {
      this.cancelamentoErro = true;
      this.cancelamentoErroMensagem = 'Não foi possível identificar a venda para cancelamento.';
      if (withoutTimer) {
        this.voltarParaPerfilAluno();
      }
      return;
    }

    this.cancelandoVenda = true;
    this.cancelamentoErro = false;
    this.cancelamentoErroMensagem = '';

    this.vendaService.cancelarVendaTerminal(vendaId).subscribe(() => {
      this.cancelamentoFinalizado();
      this.updateStatusMessages();

      if (withoutTimer) {
        this.voltarParaPerfilAluno();
      } else {
        setTimeout(() => {
          location.href = this.origemActuar;
        }, 5000);
      }
    }, (err) => {
      console.error(err);
      this.cancelandoVenda = false;
      this.cancelamentoErro = true;
      this.cancelamentoErroMensagem = 'Não foi possível cancelar a venda. Tente novamente.';
      if (withoutTimer) {
        this.voltarParaPerfilAluno();
      }
    });
  }

  private obterVendaId(): string | null {
    const possibleIds = [
      this.paymentData && this.paymentData.VendaId,
      this.paymentData && this.paymentData.Id,
      this.paymentData && this.paymentData.ID,
      this.token && this.token.VendaId,
      this.token && this.token.Id
    ];

    const found = possibleIds.find((value) => value !== undefined && value !== null);

    return found ? found.toString() : null;
  }
}
